import {ModalState} from "../../../types/modals";
import {LinkLanguageUrl, PlainDetails} from "../../../types/admin";
import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {api} from "../../api";
import {toastSuccess} from "../admin";
import {deleteResult, updateResults} from "../adminList";


export type LinkModalState = ModalState & {
    link_id: string | null
    description: string
    game: PlainDetails | null
    game_options: PlainDetails[]
    selected_teams: PlainDetails[]
    team_options: PlainDetails[]
    max_usages: number
    expires: string
    is_anonymous: boolean
    delete_message: string
    url: string
    language_urls: LinkLanguageUrl[]
}

const initialState: LinkModalState = {
    loading: false,
    isOpen: false,
    error: null,
    link_id: null,
    description: '',
    game: null,
    game_options: [],
    team_options: [],
    selected_teams: [],
    max_usages: 0,
    expires: '',
    is_anonymous: false,
    delete_message: '',
    url: '',
    language_urls: []
}

export const getLink = createAsyncThunk(
    'linkModal/getLink',
    async (link_id: string, thunkAPI) => {
        try {
            return await api.get(`/API/admin/links/${link_id}/`);
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export type NewLinkPayload = {
    description: string
    game: PlainDetails | null
    selected_teams: PlainDetails[]
    max_usages: number
    expires: string
    is_anonymous: boolean
}

export const saveNewLink = createAsyncThunk(
    'linkModal/saveNewLink',
    async (payload: NewLinkPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.post('/API/admin/links/', payload);
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export type EditLinkPayload = NewLinkPayload & {
    link_id: string
}

export const saveEditLink = createAsyncThunk(
    'linkModal/saveEditLink',
    async (payload: EditLinkPayload, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.patch(`/API/admin/links/${payload.link_id}/`, payload);
            dispatch(updateResults({result: response}));
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

export const deleteLink = createAsyncThunk(
    'linkModal/deleteLink',
    async (link_id: string, thunkAPI) => {
        try {
            const dispatch = thunkAPI.dispatch;
            const response = await api.delete(`/API/admin/links/${link_id}/`);
            if(response.key){
                dispatch(deleteResult({...response}));
            }
            if(response.success_message){
                dispatch(toastSuccess(response.success_message));
            }
            return response;
        } catch (error: any) {
            return thunkAPI.rejectWithValue(error.message);
        }
    }
);

type ModalPayload = {
    link_id: string | null
}

const slice = createSlice({
    name: 'linkModal',
    initialState,
    reducers: {
        openModal: (state, action: PayloadAction<ModalPayload>) => {
            state.isOpen = true;
            state.link_id = action.payload.link_id;
        },
        closeModal: () => {
            return initialState;
        },
        changeLinkModal: (state, action: PayloadAction<Partial<LinkModalState>>) => {
            return {
                ...state,
                ...action.payload
            };
        }
    },
    extraReducers: builder => {
        builder.addCase(getLink.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(getLink.fulfilled, (state, action) => {
            state.loading = false;
            state.description = action.payload.description;
            state.game = action.payload.game;
            state.selected_teams = action.payload.selected_teams;
            state.max_usages = action.payload.max_usages;
            state.expires = action.payload.end_date;
            state.is_anonymous = action.payload.is_anonymous
            state.team_options = action.payload.team_options || [];
            state.game_options = action.payload.game_options || [];
            state.url = action.payload.url;
            state.language_urls = action.payload.language_urls || [];
        });
        builder.addCase(getLink.rejected, (state, action) => {
            state.loading = false;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
        builder.addCase(saveNewLink.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(saveNewLink.fulfilled, () => {
            return initialState;
        });
        builder.addCase(saveNewLink.rejected, (state, action) => {
            state.loading = false;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
        builder.addCase(saveEditLink.pending, (state) => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(saveEditLink.fulfilled, () => {
            return initialState;
        });
        builder.addCase(saveEditLink.rejected, (state, action) => {
            state.loading = false;
            if (typeof action.payload === 'string') {
                state.error = action.payload as string;
            } else if (typeof action.error?.message === 'string') {
                state.error = action.error.message;
            }
        });
        builder.addCase(deleteLink.fulfilled, () => {
            return initialState;
        });
    }
});


export const {openModal, closeModal, changeLinkModal} = slice.actions;
export default slice.reducer;
